import type { CapiMilestone, ServerPixel, BinomEvent, PublicPlayerConfig } from "./types";
import { effectiveAspect, fakeExponent, toDisplayed, toReal } from "./types";
import { ICONS, playerCss } from "./styles";
import { Tracker } from "./tracker";
import { Revealer } from "./reveal";
import { lsGet, lsSet } from "./storage";

type HlsConfig = { maxBufferLength: number; maxMaxBufferLength: number };
type HlsLike = {
  config: HlsConfig;
  nextLevel: number;
  startLevel: number;
  loadSource(u: string): void;
  attachMedia(v: HTMLVideoElement): void;
  destroy(): void;
  recoverMediaError(): void;
  on(ev: string, cb: (...a: unknown[]) => void): void;
  levels: { height: number; width: number; url?: string[]; uri?: string }[];
  autoLevelCapping: number;
};
type HlsCtor = { new (cfg: object): HlsLike; isSupported(): boolean; Events: Record<string, string> };

declare global {
  interface Window {
    Hls?: HlsCtor;
    fbq?: (...a: unknown[]) => void;
    gtag?: (...a: unknown[]) => void;
    dataLayer?: unknown[];
    /** TikTok pixel, when the landing page installed it. */
    ttq?: { track: (event: string, props?: unknown, opts?: { event_id?: string }) => void };
  }
}

let hlsPromise: Promise<HlsCtor | null> | null = null;
function loadHls(base: string): Promise<HlsCtor | null> {
  if (window.Hls) return Promise.resolve(window.Hls);
  hlsPromise ??= new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = base + "hls.js";
    s.async = true;
    s.onload = () => resolve(window.Hls ?? null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return hlsPromise;
}

const html = (s: string) => {
  const t = document.createElement("template");
  t.innerHTML = s.trim();
  return t.content.firstElementChild as HTMLElement;
};

const fmt = (t: number) => {
  const s = Math.max(0, Math.floor(t));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * States:
 *  - "preview": muted loop of the first seconds until the viewer interacts (not counted as a play)
 *  - "idle":    poster + play button (autoplay blocked by the browser)
 *  - "resume":  returning viewer — continue / restart prompt
 *  - "playing" / "paused" / "ended"
 */
type State = "idle" | "preview" | "resume" | "playing" | "paused" | "ended";

/** Meta's standard events go through fbq("track"); everything else is custom. */
const STANDARD_FB_EVENTS = new Set([
  "AddPaymentInfo", "AddToCart", "AddToWishlist", "CompleteRegistration", "Contact", "CustomizeProduct",
  "Donate", "FindLocation", "InitiateCheckout", "Lead", "Purchase", "Schedule", "Search",
  "StartTrial", "SubmitApplication", "Subscribe", "ViewContent",
]);

function readCookie(name: string): string | undefined {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]).slice(0, 256) : undefined;
}

export class VslPlayer {
  private video!: HTMLVideoElement;
  private box!: HTMLElement;
  private layer!: HTMLElement;
  private fill!: HTMLElement;
  private bar!: HTMLElement;
  private ctrls!: HTMLElement;
  private muteBtn!: HTMLButtonElement;
  private timeEl: HTMLElement | null = null;
  private spinner!: HTMLElement;
  private hls: HlsLike | null = null;
  /** Guards against a refresh storm when the media link goes bad. */
  private refreshing = false;
  private refreshes = 0;
  /** Each revealed block group calls back; the pitch is one moment per view. */
  private pitchSent = false;
  private state: State = "idle";
  private tracker: Tracker;
  private revealer: Revealer;
  private k: number;
  private maxReached = 0;
  private lastTime = 0;
  private startedOnce = false;
  private sentMarks = new Set<number>();
  private posKey: string;
  private duration: number;
  private offClick: () => void = () => {};
  private sentTracker = new Set<BinomEvent>();
  // traffic: exact bytes from hls.js, otherwise seconds played x bitrate
  private measuredKb = 0;
  private playedSec = 0;
  private lastTick = 0;
  private previewCounted = 0;

  constructor(
    private host: HTMLElement,
    private root: ShadowRoot,
    private cfg: PublicPlayerConfig,
    private base: string,
    private preview: boolean,
  ) {
    const v = cfg.video;
    this.duration = v.duration || 0;
    this.k = cfg.settings.fakeProgress ? fakeExponent(cfg.settings.fakeProgressStrength) : 1;
    this.posKey = `vsl:pos:${cfg.id}:${v.id}`;
    // Binom substitutes {clickid} in the landing code; an unreplaced token is ignored.
    const attrClick = host.getAttribute("data-clickid")?.trim();
    const clickId = attrClick && !/^\{.*\}$/.test(attrClick) ? attrClick.slice(0, 128) : undefined;
    this.tracker = new Tracker(cfg.trackUrl, cfg.id, v.id, this.duration, !preview, clickId);
    this.revealer = new Revealer(cfg.id, preview ? 0 : cfg.settings.revealAt, (live) => {
      this.emit("reveal");
      // pitch time may be set in the panel or on the landing (data-vsl-delay / displayHiddenElements)
      if (live && !this.pitchSent) {
        this.pitchSent = true;
        this.trackerEvent("pitch");
        this.pixel("Pitch", "pitch");
      }
    });
    this.render();
    this.attachSource();
    if (!preview) this.trackOfferClicks();
    this.boot();
    this.emit("ready", { video: v.id, session: this.tracker.sessionId });
    host.dispatchEvent(new CustomEvent("player:ready", { detail: { video: v.id } })); // vturb-compatible name
  }

  // ---------- rendering ----------

  private render(): void {
    const s = this.cfg.settings;
    this.root.innerHTML = "";
    const style = document.createElement("style");
    style.textContent = playerCss({
      ...s,
      aspectRatio: effectiveAspect(s.aspectRatio, this.cfg.video.width, this.cfg.video.height),
    });
    this.root.appendChild(style);

    this.box = html('<div class="box" part="box"></div>');
    this.video = document.createElement("video");
    this.video.setAttribute("playsinline", "");
    this.video.setAttribute("webkit-playsinline", "");
    this.video.preload = "none";
    this.video.disablePictureInPicture = true;
    this.video.setAttribute("controlslist", "nodownload noplaybackrate");
    this.video.oncontextmenu = (e) => e.preventDefault();
    this.box.appendChild(this.video);

    this.spinner = html('<div class="spin hidden"></div>');
    this.box.appendChild(this.spinner);

    this.layer = html('<div class="layer"></div>');
    this.box.appendChild(this.layer);

    this.bar = html(`<div class="bar${s.allowSeek ? " seek" : ""} hidden"><div class="fill"></div></div>`);
    this.fill = this.bar.firstElementChild as HTMLElement;
    this.box.appendChild(this.bar);

    this.ctrls = html('<div class="ctrls hidden"></div>');
    if (s.showTime) {
      this.timeEl = html('<span class="time"></span>');
      this.ctrls.appendChild(this.timeEl);
    }
    this.muteBtn = html(`<button aria-label="Sound">${ICONS.sound}</button>`) as HTMLButtonElement;
    this.muteBtn.onclick = (e) => {
      e.stopPropagation();
      this.video.muted = !this.video.muted;
      this.muteBtn.innerHTML = this.video.muted ? ICONS.muted : ICONS.sound;
      if (!this.video.muted) this.tracker.update({ um: 1 });
    };
    this.ctrls.appendChild(this.muteBtn);
    const fsBtn = html(`<button aria-label="Fullscreen">${ICONS.fs}</button>`);
    fsBtn.onclick = (e) => {
      e.stopPropagation();
      this.toggleFullscreen();
    };
    this.ctrls.appendChild(fsBtn);
    this.box.appendChild(this.ctrls);

    this.root.appendChild(this.box);

    if (s.allowSeek) {
      this.bar.onclick = (e) => {
        e.stopPropagation();
        const r = this.bar.getBoundingClientRect();
        const f = (e.clientX - r.left) / r.width;
        const d = this.dur();
        if (d) this.video.currentTime = toReal(f, this.k) * d;
      };
    }

    this.box.addEventListener("click", () => this.onBoxClick());
    this.bindVideo();
  }

  private showLayer(kind: "none" | "play" | "unmute" | "resume" | "replay"): void {
    const s = this.cfg.settings;
    const L = this.layer;
    L.className = "layer";
    L.innerHTML = "";
    if (kind === "none") {
      L.classList.add("hidden");
      return;
    }
    if (kind === "play") {
      const b = s.playButton;
      L.innerHTML = `<div class="play${b.pulse ? " pulse" : ""}">${ICONS.play}</div>${
        b.text ? `<div class="ptext">${escapeHtml(b.text)}</div>` : ""
      }`;
    }
    if (kind === "replay") L.innerHTML = `<div class="play">${ICONS.restart}</div>`;
    if (kind === "unmute") {
      const u = s.unmute;
      L.innerHTML = `<div class="unmute${u.pulse ? " beat" : ""}">${u.title ? `<b>${escapeHtml(u.title)}</b>` : ""}${ICONS.unmute}${
        u.subtitle ? `<span>${escapeHtml(u.subtitle)}</span>` : ""
      }</div>`;
    }
    if (kind === "resume") {
      L.classList.add("resume");
      L.innerHTML = `<b>${escapeHtml(s.resumeTitle)}</b>`;
      const cont = html(`<button class="rbtn main">${ICONS.play}${escapeHtml(s.resumeContinue)}</button>`);
      const rest = html(`<button class="rbtn alt">${ICONS.restart}${escapeHtml(s.resumeRestart)}</button>`);
      cont.onclick = (e) => {
        e.stopPropagation();
        this.startPlayback(Number(lsGet(this.posKey)) || 0);
      };
      rest.onclick = (e) => {
        e.stopPropagation();
        this.startPlayback(0);
      };
      L.append(cont, rest);
    }
  }

  private setChrome(visible: boolean): void {
    this.bar.classList.toggle("hidden", !visible);
    this.ctrls.classList.toggle("hidden", !visible);
  }

  // ---------- source ----------

  private attachSource(): void {
    const v = this.cfg.video;
    const canNative = !!this.video.canPlayType("application/vnd.apple.mpegurl");
    if (v.hls && canNative) {
      this.video.src = v.hls;
      this.video.onerror = () => void this.refreshSource();
      return;
    }
    if (v.hls) {
      loadHls(this.base).then((Hls) => {
        if (Hls && Hls.isSupported()) {
          const cap = this.cfg.settings.maxQuality;
          const hls = new Hls({
            capLevelToPlayerSize: true,
            // the muted preview loop only needs the first seconds in the cheapest quality
            maxBufferLength: this.state === "preview" ? 4 : 20,
            maxMaxBufferLength: 40,
            startLevel: 0,
            // keep the preview loop in memory so it doesn't re-download every lap
            backBufferLength: 60,
          });
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // Usually already capped by the server playlist; this is a fallback for direct Bunny URLs.
            // Bunny labels renditions "<N>p/"; vertical videos have height > N, so compare labels.
            if (cap > 0) {
              let idx = -1;
              hls.levels.forEach((l, i) => {
                const uri = l.uri ?? l.url?.[0] ?? "";
                const label = Number(/(\d+)p\//.exec(uri)?.[1] ?? Math.min(l.width || l.height, l.height));
                if (label <= cap) idx = i;
              });
              if (idx >= 0) hls.autoLevelCapping = idx;
            }
          });
          hls.on(Hls.Events.FRAG_LOADED, (...args: unknown[]) => {
            const d = args[1] as { payload?: ArrayBuffer; frag?: { stats?: { loaded?: number } } };
            // payload is already transferred to the demuxer (byteLength 0) — stats hold the real size
            const bytes = Math.max(d?.frag?.stats?.loaded ?? 0, d?.payload?.byteLength ?? 0);
            this.measuredKb += bytes / 1024;
            this.tracker.setTraffic(this.measuredKb);
          });
          hls.on(Hls.Events.ERROR, (...args: unknown[]) => {
            const d = args[1] as { fatal?: boolean; type?: string };
            if (!d?.fatal) return;
            // A signed media link expires after some hours; fetching the config
            // again yields a fresh one and playback continues from the same second.
            if (d.type === "networkError") void this.refreshSource();
            else if (d.type === "mediaError") hls.recoverMediaError();
          });
          hls.loadSource(v.hls!);
          hls.attachMedia(this.video);
          this.hls = hls;
        } else {
          void this.useMp4Fallback();
        }
      });
      return;
    }
    void this.useMp4Fallback();
  }

  /**
   * Re-reads the config and reattaches the video at the same position. Used when
   * the media link stops working mid-session (an expired signature is the usual
   * reason). Gives up after a few tries so a real outage can't loop forever.
   */
  private async refreshSource(): Promise<void> {
    if (this.refreshing || this.refreshes >= 3) return;
    this.refreshing = true;
    this.refreshes++;
    const at = this.video.currentTime;
    const wasPlaying = !this.video.paused && !this.video.ended;
    try {
      const res = await fetch(`${this.base}/api/p/${encodeURIComponent(this.cfg.id)}`, {
        credentials: "omit",
        cache: "no-store",
      });
      if (!res.ok) return;
      const fresh = (await res.json()) as { video?: PublicPlayerConfig["video"] };
      if (!fresh.video?.hls && !fresh.video?.mp4) return;
      this.cfg = { ...this.cfg, video: fresh.video };
      this.hls?.destroy();
      this.hls = null;
      this.video.onerror = null;
      this.attachSource();
      const resume = () => {
        this.video.removeEventListener("loadedmetadata", resume);
        if (at > 0) this.video.currentTime = at;
        if (wasPlaying) void this.video.play().catch(() => {});
      };
      this.video.addEventListener("loadedmetadata", resume);
    } catch {
      /* offline or the panel is down — the next error will try again */
    } finally {
      this.refreshing = false;
    }
  }

  /**
   * The direct file is not part of the public config on purpose — a single mp4
   * link is trivial to save or hotlink. It is fetched only by the rare browser
   * that can play neither native HLS nor hls.js.
   */
  private async useMp4Fallback(): Promise<void> {
    if (this.cfg.video.mp4) {
      this.video.src = this.cfg.video.mp4;
      return;
    }
    try {
      const res = await fetch(`${this.base}/api/p/${encodeURIComponent(this.cfg.id)}/mp4`, { credentials: "omit" });
      const data = (await res.json()) as { mp4?: string };
      if (data.mp4) this.video.src = data.mp4;
    } catch {
      /* nothing playable — the poster layer stays up */
    }
  }

  // ---------- flow ----------

  private boot(): void {
    const s = this.cfg.settings;
    const saved = Number(lsGet(this.posKey)) || 0;
    const d = this.dur();
    if (s.resume && !this.preview && saved > 5 && (!d || saved < d - 5)) {
      this.state = "resume";
      this.showLayer("resume");
      return;
    }
    this.startPreview();
  }

  /** Autoplay was refused: show the poster (if any) with a play button. */
  private toIdle(): void {
    this.state = "idle";
    this.setChrome(false);
    if (this.cfg.video.poster) this.video.poster = this.cfg.video.poster;
    this.showLayer("play");
  }

  private startPreview(): void {
    const v = this.video;
    this.state = "preview";
    v.muted = true;
    v.preload = "auto";
    this.setChrome(false);
    this.showLayer(this.cfg.settings.autoplay === "smart" ? "unmute" : "play");
    const p = v.play();
    if (p) p.then(() => this.tracker.update({ ap: 1 })).catch(() => this.toIdle()); // e.g. iOS low-power mode
    else this.tracker.update({ ap: 1 });
  }

  private startPlayback(from: number): void {
    const v = this.video;
    this.state = "playing";
    v.muted = false;
    this.muteBtn.innerHTML = ICONS.sound;
    this.showLayer("none");
    this.setChrome(true);
    this.lastTime = from;
    this.maxReached = Math.max(this.maxReached, from);
    try {
      v.currentTime = from;
    } catch {
      v.addEventListener("loadedmetadata", () => (v.currentTime = from), { once: true });
    }
    if (this.hls) {
      // real playback: normal buffer and automatic quality again
      this.hls.config.maxBufferLength = 20;
      this.hls.nextLevel = -1;
    }
    if (!this.startedOnce) {
      this.startedOnce = true;
      this.tracker.update({ ap: 1, st: 1, sp: Math.floor(from) });
      this.pixel("Play", "play");
      this.trackerEvent("play");
      this.emit("play");
    }
    v.play()
      // "Sound on" is recorded only once playback really started unmuted: a
      // browser may refuse an unmuted start, and marking it upfront made the
      // metric always read 100%.
      .then(() => {
        if (!v.muted) this.tracker.update({ um: 1 });
      })
      .catch(() => {
        // Some browsers refuse unmuted play without a fresh gesture; fall back to muted.
        v.muted = true;
        this.muteBtn.innerHTML = ICONS.muted;
        v.play().catch(() => this.toIdle());
      });
  }

  private onBoxClick(): void {
    switch (this.state) {
      case "preview":
      case "idle":
      case "ended":
        this.startPlayback(0);
        break;
      case "playing":
        if (this.cfg.settings.allowPause) this.video.pause();
        break;
      case "paused":
        this.state = "playing";
        this.showLayer("none");
        this.video.play().catch(() => {});
        break;
    }
  }

  private bindVideo(): void {
    const v = this.video;
    v.addEventListener("waiting", () => {
      if (this.state === "playing") this.spinner.classList.remove("hidden");
    });
    v.addEventListener("playing", () => this.spinner.classList.add("hidden"));
    v.addEventListener("canplay", () => this.spinner.classList.add("hidden"));
    v.addEventListener("loadedmetadata", () => {
      if (!this.duration && v.duration && isFinite(v.duration)) {
        this.duration = v.duration;
        this.tracker.update({ dur: Math.round(v.duration) });
      }
    });
    v.addEventListener("pause", () => {
      if (this.state === "playing" && !v.ended) {
        this.state = "paused";
        this.showLayer("play");
      }
    });
    v.addEventListener("seeking", () => {
      if (this.state !== "playing" && this.state !== "paused") return;
      // Without seek allowed, block jumping ahead (keyboard, iOS native controls).
      if (!this.cfg.settings.allowSeek && v.currentTime > this.maxReached + 1.5) {
        v.currentTime = this.maxReached;
      }
    });
    v.addEventListener("timeupdate", () => this.onTime());
    v.addEventListener("ended", () => this.onEnded());
  }

  private onTime(): void {
    const v = this.video;
    this.countTraffic();
    if (this.state === "preview") {
      const loop = this.cfg.settings.previewLoop;
      if (loop > 0 && v.currentTime >= loop) v.currentTime = 0;
      return;
    }
    if (this.state !== "playing" && this.state !== "paused") return;
    const t = v.currentTime;
    const dt = t - this.lastTime;
    if (dt > 0 && dt < 2 && !v.paused) this.tracker.addWatched(dt);
    this.lastTime = t;
    if (t > this.maxReached) this.maxReached = t;
    this.tracker.position(this.maxReached);

    const d = this.dur();
    if (d) {
      this.fill.style.width = (toDisplayed(t / d, this.k) * 100).toFixed(2) + "%";
      if (this.timeEl) this.timeEl.textContent = `${fmt(t)} / ${fmt(d)}`;
      const pct = (this.maxReached / d) * 100;
      for (const m of [25, 50, 75, 95]) {
        if (pct >= m && !this.sentMarks.has(m)) {
          this.sentMarks.add(m);
          this.pixel("Watch" + m, `p${m}` as CapiMilestone);
          this.trackerEvent(`p${m}` as BinomEvent);
          this.emit("progress", { percent: m });
        }
      }
      if (t > 5 && t < d - 5) lsSet(this.posKey, String(Math.floor(t)));
    }
    if (!this.preview) this.revealer.tick(this.maxReached);
  }

  private onEnded(): void {
    if (this.state === "preview") {
      // video shorter than the preview loop
      this.video.currentTime = 0;
      this.video.play().catch(() => {});
      return;
    }
    lsSet(this.posKey, "0");
    this.state = "ended";
    this.tracker.position(this.dur());
    this.tracker.flush(false);
    this.emit("ended");
    if (!this.preview) this.revealer.revealAll();
    this.showLayer(this.cfg.settings.endAction === "replay" ? "replay" : "none");
  }

  private countTraffic(): void {
    const t = this.video.currentTime;
    const dt = t - this.lastTick;
    this.lastTick = t;
    if (this.hls || dt <= 0 || dt > 2) return; // hls.js reports real bytes
    if (this.state === "preview") {
      // the looped part is served from the browser cache after the first lap
      if (t <= this.previewCounted) return;
      this.previewCounted = t;
    }
    this.playedSec += dt;
    const kbps = this.cfg.video.kbps || 1500;
    // +15 s for what the browser buffers ahead
    this.tracker.setTraffic(((this.playedSec + 15) * kbps) / 8);
  }

  /** Clicks on links/buttons inside pitch-time blocks count as "offer clicks". */
  private trackOfferClicks(): void {
    const handler = (e: Event) => {
      const t = e.target as Element | null;
      const hit = t?.closest?.("a, button, [data-vsl-cta]");
      if (hit && (hit.closest(".vsl-hide, [data-vsl-delay], [data-vsl-revealed]") || hit.hasAttribute("data-vsl-cta"))) {
        this.tracker.update({ oc: 1 });
        this.tracker.flush(true);
        this.pixel("OfferClick", "offer");
        this.trackerEvent("offer");
        this.emit("offer-click");
      }
    };
    document.addEventListener("click", handler, true);
    this.offClick = () => document.removeEventListener("click", handler, true);
  }

  // ---------- helpers ----------

  private dur(): number {
    const v = this.video?.duration;
    return v && isFinite(v) ? v : this.duration;
  }

  private toggleFullscreen(): void {
    const v = this.video as HTMLVideoElement & { webkitEnterFullscreen?: () => void };
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    } else if (this.box.requestFullscreen) {
      this.box.requestFullscreen().catch(() => v.webkitEnterFullscreen?.());
    } else {
      v.webkitEnterFullscreen?.(); // iOS Safari
    }
  }

  /**
   * Browser pixels, plus the server-side copy when the account enabled it.
   *
   * With the Conversions API on, the pixel uses the platform's event name and
   * an explicit event id; the server sends the same pair. That is the only way
   * the platform can tell the two copies apart from two real actions.
   */
  private pixel(name: string, milestone?: CapiMilestone): void {
    if (this.preview) return;
    const server = this.cfg.serverPixel;
    const mapped = milestone && server?.events?.[milestone];
    const p = this.cfg.settings.pixels;
    const eventId = milestone ? `${this.tracker.sessionId}.${milestone}` : undefined;

    if (mapped) {
      const data = { player: this.cfg.id };
      try {
        if (server!.platform === "tiktok") {
          // TikTok takes the id in the third argument, Meta in an options object.
          window.ttq?.track(mapped, data, { event_id: eventId });
        } else {
          // Standard names go through track(), anything else is a custom event.
          const std = STANDARD_FB_EVENTS.has(mapped);
          window.fbq?.(std ? "track" : "trackCustom", mapped, data, eventId ? { eventID: eventId } : undefined);
        }
      } catch {
        /* ignore third-party errors */
      }
      void this.sendServerPixel(server!, milestone!, eventId!);
    } else if (p.enabled) {
      const ev = `${p.prefix}${name}`;
      const data = { player: this.cfg.id };
      try {
        window.fbq?.("trackCustom", ev, data);
      } catch {
        /* ignore third-party errors */
      }
    }

    // Google keeps the prefixed names so existing GA reports stay comparable.
    if (!p.enabled) return;
    const ev = `${p.prefix}${name}`;
    const data = { player: this.cfg.id };
    try {
      window.gtag?.("event", ev, data);
      if (!window.gtag) window.dataLayer?.push({ event: ev, ...data });
    } catch {
      /* ignore third-party errors */
    }
  }

  /** Fire-and-forget: the viewer must never wait for an ad platform. */
  private sendServerPixel(server: ServerPixel, milestone: CapiMilestone, eventId: string): void {
    const body = JSON.stringify({
      p: this.cfg.id,
      s: this.tracker.sessionId,
      e: milestone,
      eid: eventId,
      url: location.href.slice(0, 500),
      fbp: readCookie("_fbp"),
      fbc: readCookie("_fbc"),
      ttp: readCookie("_ttp"),
      ttclid: new URLSearchParams(location.search).get("ttclid")?.slice(0, 200) || readCookie("ttclid"),
    });
    try {
      const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
      if (navigator.sendBeacon?.(server.url, blob)) return;
    } catch {
      /* fall through */
    }
    try {
      fetch(server.url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "text/plain" } }).catch(() => {});
    } catch {
      /* nothing else to try */
    }
  }

  /** Milestone -> tracker (Binom) custom event, once per page view, only with a click id. */
  private trackerEvent(name: BinomEvent): void {
    const t = this.cfg.tracker;
    const n = t?.events[name];
    const ck = this.tracker.clickId;
    if (!t || !n || !ck || this.preview || this.sentTracker.has(name)) return;
    this.sentTracker.add(name);

    if (t.relayUrl) {
      // Our server forwards it to the tracker, so the tracker's domain never
      // appears in the page. text/plain keeps it a simple request (no preflight,
      // which sendBeacon cannot do).
      const body = JSON.stringify({ p: this.cfg.id, s: this.tracker.sessionId, e: name, ck });
      try {
        const blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
        if (navigator.sendBeacon?.(t.relayUrl, blob)) return;
      } catch {
        /* fall through to fetch */
      }
      try {
        fetch(t.relayUrl, { method: "POST", body, keepalive: true, headers: { "Content-Type": "text/plain" } }).catch(() => {});
      } catch {
        /* nothing else to try */
      }
      return;
    }

    if (!t.urlTemplate) return;
    const url = t.urlTemplate.replace(/\{clickid\}/g, encodeURIComponent(ck)).replace(/\{n\}/g, String(n));
    try {
      fetch(url, { mode: "no-cors", keepalive: true, credentials: "include" }).catch(() => {});
    } catch {
      new Image().src = url;
    }
  }

  private emit(name: string, detail: object = {}): void {
    this.host.dispatchEvent(new CustomEvent("vsl:" + name, { detail, bubbles: true }));
  }

  destroy(): void {
    this.offClick();
    this.tracker.destroy();
    this.hls?.destroy();
    this.video.removeAttribute("src");
    this.video.load();
  }

  /**
   * Jump to a moment (testing pitch time, bookmarklet). Bypasses the "no seeking" rule
   * and counts the skipped part as reached, so pitch-time blocks open.
   */
  seekTo(seconds: number): void {
    const v = this.video;
    const d = this.dur();
    const t = Math.max(0, d ? Math.min(seconds, d - 1) : seconds);
    if (this.state !== "playing" && this.state !== "paused") this.startPlayback(t);
    this.maxReached = Math.max(this.maxReached, t);
    this.lastTime = t;
    v.currentTime = t;
    this.onTime();
  }

  /** vturb-compatible: displayHiddenElements(seconds, [".esconder"], { persist: true, callback }) */
  displayHiddenElements(
    seconds: number | string,
    selectors: string[] | string,
    opts?: { persist?: boolean; callback?: () => void },
  ): void {
    const at =
      typeof seconds === "number" ? seconds : String(seconds).split(":").reduce((a, p) => a * 60 + Number(p || 0), 0);
    const list = Array.isArray(selectors) ? selectors : [selectors];
    this.revealer.addRule(Number.isFinite(at) ? at : 0, list, !!opts?.persist, opts?.callback);
  }

  // Public API (el.player.*)
  get currentTime(): number {
    return this.video.currentTime;
  }
  get sessionId(): string {
    return this.tracker.sessionId;
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
